// ForgotPasswordPage.jsx
// Página para solicitar reset de contraseña.
// Diseño neumorphic — mismo sistema de diseño que LoginPage.

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './ForgotPasswordPage.css';
import { enlaceWhatsApp } from '../contacto.js';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const WHATSAPP_NUMBER = "5215610480746";
const waSupport = `¡Hola! Tengo problemas para recuperar mi contraseña de Spacy. ¿Me pueden ayudar?`;
const openWhatsApp = (msg) =>
    window.open(enlaceWhatsApp(msg, WHATSAPP_NUMBER), '_blank');

const WaIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

// Estados posibles del avatar (reutiliza las mismas imágenes que LoginPage)
const AVATAR = {
    idle: '/intro.png',
    writing: '/escribir.png',
    success: '/listo.png',
    error: '/fallo.png',
};

function ForgotPasswordPage() {
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [avatar, setAvatar] = useState('idle');
    const [phase, setPhase] = useState('form');   // 'form' | 'loading' | 'sent' | 'error'
    const [cooldown, setCooldown] = useState(0);        // segundos de espera entre intentos
    const cooldownRef = useRef(null);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        document.body.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    // Limpia el cooldown timer al desmontar
    useEffect(() => () => clearInterval(cooldownRef.current), []);

    const startCooldown = (seconds = 60) => {
        setCooldown(seconds);
        clearInterval(cooldownRef.current);
        cooldownRef.current = setInterval(() => {
            setCooldown(prev => {
                if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
                return prev - 1;
            });
        }, 1000);
    };

    const validateEmail = () => {
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            setEmailError('Por favor, introduce un email válido.');
            return false;
        }
        setEmailError('');
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateEmail()) {
            setAvatar('error');
            setTimeout(() => setAvatar('idle'), 2000);
            return;
        }
        if (cooldown > 0) return;

        setPhase('loading');
        setAvatar('writing');

        try {
            const res = await fetch(`${API_BASE_URL}/api/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            // El backend siempre devuelve 200 (anti-enumeration)
            // Cualquier respuesta distinta de error de red = éxito para el usuario
            if (res.ok || res.status === 429) {
                if (res.status === 429) {
                    // Rate limit alcanzado
                    setPhase('error');
                    setAvatar('error');
                    setEmailError('Demasiados intentos. Espera un momento y vuelve a intentarlo.');
                    setTimeout(() => { setPhase('form'); setAvatar('idle'); }, 4000);
                    return;
                }
                setPhase('sent');
                setAvatar('success');
                startCooldown(60); // 60 segundos antes de poder reenviar
            } else {
                throw new Error('Server error');
            }
        } catch {
            setPhase('error');
            setAvatar('error');
            setEmailError('No se pudo conectar al servidor. Inténtalo más tarde.');
            setTimeout(() => { setPhase('form'); setAvatar('idle'); }, 3000);
        }
    };

    const handleResend = () => {
        if (cooldown > 0) return;
        setPhase('form');
        setAvatar('idle');
        setEmailError('');
    };

    return (
        <div className="fgp-page">
            <div className="fgp-container">

                {/* Avatar reactivo */}
                <div className="fgp-avatar-wrap">
                    <img
                        src={AVATAR[avatar]}
                        alt="Avatar"
                        className="fgp-avatar-img"
                        key={avatar}
                    />
                </div>

                {/* Toggle de tema */}
                <div className="fgp-theme-row">
                    <button
                        className="fgp-theme-btn"
                        onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
                        aria-label="Cambiar tema"
                    >
                        {theme === 'light' ? '🌙' : '☀️'}
                    </button>
                </div>

                {/* ── FASE: FORMULARIO ── */}
                {(phase === 'form' || phase === 'loading' || phase === 'error') && (
                    <>
                        <div className="fgp-header">
                            <div className="fgp-icon-badge">🔑</div>
                            <h1 className="fgp-title">¿Olvidaste tu contraseña?</h1>
                            <p className="fgp-subtitle">
                                Escribe tu correo y te enviaremos un enlace para restablecerla.
                                Expira en <strong>15 minutos</strong>.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} noValidate className="fgp-form">
                            <div className="fgp-input-group">
                                <label htmlFor="fgp-email" className="fgp-label">
                                    Correo electrónico
                                </label>
                                <input
                                    maxLength={254}
                                    id="fgp-email"
                                    type="email"
                                    className={`fgp-input ${emailError ? 'fgp-input--error' : ''}`}
                                    placeholder="tucorreo@ejemplo.com"
                                    value={email}
                                    onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                                    onFocus={() => setAvatar('writing')}
                                    onBlur={() => phase === 'form' && setAvatar('idle')}
                                    autoComplete="email"
                                    disabled={phase === 'loading'}
                                />
                                {emailError && (
                                    <p className="fgp-error" role="alert">{emailError}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="fgp-submit-btn"
                                disabled={phase === 'loading' || cooldown > 0}
                            >
                                {phase === 'loading' ? (
                                    <span className="fgp-spinner-wrap">
                                        <span className="fgp-spinner" />
                                        Enviando...
                                    </span>
                                ) : cooldown > 0 ? (
                                    `Reenviar en ${cooldown}s`
                                ) : (
                                    '📧 Enviar enlace'
                                )}
                            </button>
                        </form>
                    </>
                )}

                {/* ── FASE: ENVIADO EXITOSAMENTE ── */}
                {phase === 'sent' && (
                    <div className="fgp-success-block">
                        <div className="fgp-success-icon">📬</div>
                        <h2 className="fgp-success-title">¡Revisa tu bandeja!</h2>
                        <p className="fgp-success-body">
                            Si <strong>{email}</strong> está registrado, recibirás
                            un enlace para restablecer tu contraseña. Revisa también tu
                            carpeta de <em>spam</em>.
                        </p>

                        <div className="fgp-security-note">
                            <span className="fgp-security-icon">⏱️</span>
                            <span>El enlace es válido por <strong>15 minutos</strong> y de uso único.</span>
                        </div>

                        {cooldown > 0 ? (
                            <p className="fgp-cooldown-text">
                                ¿No llegó el correo? Podrás reenviar en{' '}
                                <strong className="fgp-cooldown-num">{cooldown}s</strong>
                            </p>
                        ) : (
                            <button className="fgp-resend-btn" onClick={handleResend}>
                                🔄 Reenviar enlace
                            </button>
                        )}
                    </div>
                )}

                {/* Siempre visible: volver al login */}
                <div className="fgp-back-row">
                    <Link to="/login" className="fgp-back-link">
                        ← Volver al login
                    </Link>
                </div>
            </div>

            {/* Footer de soporte */}
            <footer className="fgp-footer">
                <div className="fgp-support-card">
                    <span className="fgp-support-icon">🛟</span>
                    <div className="fgp-support-copy">
                        <strong>¿Problemas con el correo?</strong>
                        <span>Soporte de lunes a sábado</span>
                    </div>
                    <button
                        className="fgp-wa-btn"
                        onClick={() => openWhatsApp(waSupport)}
                        type="button"
                    >
                        <WaIcon /> Contactar
                    </button>
                </div>
            </footer>
        </div>
    );
}

export default ForgotPasswordPage;